import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineBookOpen, HiOutlinePlus, HiOutlineTrash, HiOutlineXMark,
  HiOutlineDocumentText, HiOutlineLink, HiOutlinePlayCircle,
  HiOutlineEye, HiOutlineEyeSlash, HiOutlineLockClosed, HiOutlineFolder,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { resourcesAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import toast from "react-hot-toast";
import "./AdminResources.css";

const VISIBILITY = [
  { v:"all",      label:"Everyone",        hint:"Any member with a portal account" },
  { v:"cleared",  label:"Medically Cleared", hint:"Once they've passed medical clearance" },
  { v:"assigned", label:"Confirmed",       hint:"Once they're confirmed for a retreat" },
  { v:"alumni",   label:"Alumni",          hint:"Once they've completed a retreat" },
];

const TYPE_ICON = { file: HiOutlineDocumentText, link: HiOutlineLink, video: HiOutlinePlayCircle };

const fmtSize = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${Math.round(b/1024)} KB` : `${(b/1048576).toFixed(1)} MB`;

/* ── Add resource ── */
function ResourceModal({ categories, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("file");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    if (type === "file" && !file) { setError("Choose a file to upload."); return; }
    if (type !== "file" && !url.trim()) { setError("Enter a URL."); return; }

    setSaving(true); setError("");
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("type", type);
      fd.append("visibility", visibility);
      if (categoryId) fd.append("category_id", categoryId);
      if (type === "file") fd.append("file", file);
      else fd.append("external_url", url.trim());

      await resourcesAPI.create(fd);
      toast.success("Resource added.");
      onSaved();
    } catch (err) { setError(err.message || "Upload failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"470px"}}>
        <div className="aap__contact-head">
          <h3>Add Resource</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>

        <div className="ars__field">
          <label>Title</label>
          <input className="arb__form-input" value={title} placeholder="e.g. Packing Guide"
            onChange={e=>{setTitle(e.target.value); setError("");}}/>
        </div>

        <div className="ars__field">
          <label>Description <span className="ars__optional">(optional)</span></label>
          <textarea className="arb__form-input" rows={2} value={description}
            onChange={e=>setDescription(e.target.value)}/>
        </div>

        <div className="ars__field">
          <label>Type</label>
          <div className="ars__type-row">
            {["file","link","video"].map(t => (
              <button key={t} type="button"
                className={`ars__type-btn ${type===t?"ars__type-btn--active":""}`}
                onClick={()=>{setType(t); setError("");}}>
                {t === "file" ? "Upload File" : t === "link" ? "External Link" : "Video"}
              </button>
            ))}
          </div>
        </div>

        {type === "file" ? (
          <div className="ars__field">
            <label>File</label>
            <input type="file" className="arb__form-input"
              onChange={e=>{setFile(e.target.files?.[0]||null); setError("");}}/>
            <p className="ars__hint">PDF, Word, Excel, PowerPoint, text, images or ZIP. Max 25 MB.</p>
          </div>
        ) : (
          <div className="ars__field">
            <label>URL</label>
            <input className="arb__form-input" value={url} placeholder="https://…"
              onChange={e=>{setUrl(e.target.value); setError("");}}/>
          </div>
        )}

        <div className="ars__field">
          <label>Category</label>
          <select className="arb__form-input arb__form-select" value={categoryId}
            onChange={e=>setCategoryId(e.target.value)}>
            <option value="">General (no category)</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="ars__field">
          <label>Who can see this?</label>
          <select className="arb__form-input arb__form-select" value={visibility}
            onChange={e=>setVisibility(e.target.value)}>
            {VISIBILITY.map(v => <option key={v.v} value={v.v}>{v.label}</option>)}
          </select>
          <p className="ars__hint">{VISIBILITY.find(v=>v.v===visibility)?.hint}</p>
        </div>

        {error && <p className="ars__error">{error}</p>}
        <div className="ars__modal-actions">
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Add Resource"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Add category ── */
function CategoryModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError("");
    try {
      await resourcesAPI.createCategory({ name: name.trim(), description: description.trim() || null });
      toast.success("Category created.");
      onSaved();
    } catch (err) { setError(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"400px"}}>
        <div className="aap__contact-head">
          <h3>New Category</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <div className="ars__field">
          <label>Name</label>
          <input className="arb__form-input" value={name} placeholder="e.g. Forms & Documents"
            onChange={e=>{setName(e.target.value); setError("");}}/>
        </div>
        <div className="ars__field">
          <label>Description <span className="ars__optional">(optional)</span></label>
          <input className="arb__form-input" value={description}
            onChange={e=>setDescription(e.target.value)}/>
        </div>
        {error && <p className="ars__error">{error}</p>}
        <div className="ars__modal-actions">
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminResources() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canEdit = permLoading ? true : hasPermission(admin, "resources.edit");

  const [data, setData] = useState({ categories: [], resources: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    resourcesAPI.list()
      .then(res => setData(res?.data || { categories: [], resources: [] }))
      .catch(err => toast.error(err.message || "Failed to load resources."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePublished = async (r) => {
    setBusy(true);
    try {
      await resourcesAPI.update(r.id, { published: !r.published });
      fetchData();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const remove = (r) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete "{r.title}"?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>
          {r.type === "file" ? "The uploaded file will be removed too. " : ""}This can't be undone.
        </p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async ()=>{
            toast.dismiss(t.id); setBusy(true);
            try { await resourcesAPI.remove(r.id); toast.success("Deleted."); fetchData(); }
            catch(err){ toast.error(err.message||"Failed."); }
            finally { setBusy(false); }
          }} style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete</button>
          <button onClick={()=>toast.dismiss(t.id)}
            style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"330px"} });
  };

  const { categories, resources } = data;
  const grouped = [
    ...categories.map(c => ({ ...c, items: resources.filter(r => r.category_id === c.id) })),
    { id: 0, name: "General", items: resources.filter(r => !r.category_id) },
  ].filter(g => g.items.length > 0 || g.id !== 0);

  return (
    <AdminLayout pageTitle="Resource Library" pageSubtitle="Guides, forms and materials for members."
      permission="resources.view">

      {loading ? (
        <div className="arb__loading"><div className="acr__spinner"/></div>
      ) : (
        <>
          <div className="aap__stats-row" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{background:"rgba(var(--admin-rust-rgb), 0.1)"}}>
                  <HiOutlineBookOpen style={{color:"#e05a2b",fontSize:"1.1rem"}}/>
                </div>
                <p className="aap__stat-label">Total Resources</p>
              </div>
              <p className="aap__stat-value">{resources.length}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{background:"rgba(22,163,74,0.1)"}}>
                  <HiOutlineEye style={{color:"#16a34a",fontSize:"1.1rem"}}/>
                </div>
                <p className="aap__stat-label">Published</p>
              </div>
              <p className="aap__stat-value">{resources.filter(r=>r.published).length}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{background:"rgba(107,98,88,0.1)"}}>
                  <HiOutlineFolder style={{color:"#6b6258",fontSize:"1.1rem"}}/>
                </div>
                <p className="aap__stat-label">Categories</p>
              </div>
              <p className="aap__stat-value">{categories.length}</p>
            </div>
          </div>

          {canEdit && (
            <div className="ars__toolbar">
              <button type="button" className="a-btn a-btn--outline" onClick={()=>setAddingCat(true)}>
                <HiOutlineFolder/> New Category
              </button>
              <button type="button" className="a-btn a-btn--rust" onClick={()=>setAdding(true)}>
                <HiOutlinePlus/> Add Resource
              </button>
            </div>
          )}

          {resources.length === 0 ? (
            <div className="acard arb__empty">
              <HiOutlineBookOpen className="arb__empty-icon"/>
              <p className="arb__empty-title">No resources yet</p>
              <p className="arb__empty-sub">
                Add guides, forms, or links and they'll appear in members' Resource Library.
              </p>
              {canEdit && (
                <button type="button" className="a-btn a-btn--rust" onClick={()=>setAdding(true)}>
                  <HiOutlinePlus/> Add Your First Resource
                </button>
              )}
            </div>
          ) : grouped.map(g => (
            <div key={g.id} className="acard aq__section">
              <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0.5rem"}}>{g.name}</p>
              {g.items.length === 0 ? (
                <p className="aap__placeholder" style={{padding:"1rem 1.25rem",textAlign:"left"}}>
                  Nothing in this category yet.
                </p>
              ) : g.items.map(r => {
                const Icon = TYPE_ICON[r.type] || HiOutlineDocumentText;
                const vis = VISIBILITY.find(v => v.v === r.visibility);
                return (
                  <div key={r.id} className="ars__row">
                    <div className="ars__row-icon"><Icon/></div>
                    <div className="ars__row-main">
                      <p className="ars__row-title">
                        {r.title}
                        {!r.published && <span className="ars__badge ars__badge--draft">Draft</span>}
                        {r.visibility !== "all" && (
                          <span className="ars__badge"><HiOutlineLockClosed/> {vis?.label}</span>
                        )}
                      </p>
                      {r.description && <p className="ars__row-sub">{r.description}</p>}
                      <p className="ars__row-meta">
                        {r.type === "file"
                          ? `${r.file_name || "file"}${r.file_size ? ` · ${fmtSize(r.file_size)}` : ""}`
                          : r.external_url}
                        {r.download_count > 0 && ` · ${r.download_count} download${r.download_count!==1?"s":""}`}
                      </p>
                    </div>
                    {canEdit && (
                      <div className="ars__row-actions">
                        <button type="button" className="a-btn a-btn--ghost" disabled={busy}
                          title={r.published ? "Unpublish" : "Publish"}
                          onClick={()=>togglePublished(r)}>
                          {r.published ? <HiOutlineEyeSlash/> : <HiOutlineEye/>}
                        </button>
                        <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}}
                          disabled={busy} onClick={()=>remove(r)}><HiOutlineTrash/></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}

      {adding && (
        <ResourceModal categories={categories}
          onClose={()=>setAdding(false)}
          onSaved={()=>{ setAdding(false); fetchData(); }}/>
      )}
      {addingCat && (
        <CategoryModal onClose={()=>setAddingCat(false)}
          onSaved={()=>{ setAddingCat(false); fetchData(); }}/>
      )}
    </AdminLayout>
  );
}
